/**
 * Wave 3 Block 3A — MissionContactsCard
 * 
 * Displays aggregated contacts for a mission.
 * Includes pilot, organization, insurance, and emergency contacts.
 * 
 * Item 3c: Mission Contact Aggregation
 */
import { useState, useEffect } from 'react';
import { 
  Users, Phone, Mail, User, Building, Shield, 
  AlertTriangle, Clock, ChevronDown, ChevronUp,
  Loader2, ExternalLink, Copy, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { toast } from 'sonner';
import api from '../../lib/api';

// Contact card sub-component
const ContactItem = ({ contact, role, icon: Icon, language = 'cs' }) => {
  const [copied, setCopied] = useState(false);
  
  if (!contact) return null;
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(language === 'cs' ? `${label} zkopírováno` : `${label} copied`);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{contact.name}</span>
          {role && (
            <Badge variant="outline" className="text-xs">
              {role}
            </Badge>
          )}
        </div>
        {contact.role && contact.role !== role && (
          <p className="text-xs text-gray-500">{contact.role}</p>
        )}
        <div className="mt-1 space-y-0.5">
          {contact.email && (
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3 text-gray-400" />
              <a 
                href={`mailto:${contact.email}`} 
                className="text-xs text-blue-600 hover:underline truncate"
              >
                {contact.email}
              </a>
              <button 
                onClick={() => copyToClipboard(contact.email, 'Email')}
                className="p-0.5 hover:bg-gray-200 rounded"
              >
                {copied ? <CheckCircle className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-gray-400" />}
              </button>
            </div>
          )}
          {contact.phone && (
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3 text-gray-400" />
              <a 
                href={`tel:${contact.phone}`} 
                className="text-xs text-blue-600 hover:underline"
              >
                {contact.phone}
              </a>
              <button 
                onClick={() => copyToClipboard(contact.phone, language === 'cs' ? 'Telefon' : 'Phone')}
                className="p-0.5 hover:bg-gray-200 rounded"
              >
                {copied ? <CheckCircle className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-gray-400" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Emergency contact item (different styling)
const EmergencyContactItem = ({ contact, language = 'cs' }) => {
  const isStatic = contact.is_static || contact.source === 'static';
  
  return (
    <div className={`flex items-center justify-between p-2 rounded-lg ${
      isStatic ? 'bg-gray-100' : 'bg-red-50'
    }`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className={`w-4 h-4 ${isStatic ? 'text-gray-500' : 'text-red-500'}`} />
        <div>
          <span className="text-sm font-medium">{contact.name}</span>
          {contact.notes && (
            <p className="text-xs text-gray-500">{contact.notes}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {contact.available_24_7 && (
          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
            24/7
          </Badge>
        )}
        <a 
          href={`tel:${contact.phone}`}
          className="flex items-center gap-1 px-2 py-1 bg-white rounded border text-sm font-medium hover:bg-gray-50"
        >
          <Phone className="w-3 h-3" />
          {contact.phone}
        </a>
      </div>
    </div>
  );
};

// Main component
export const MissionContactsCard = ({
  missionId,
  language = 'cs',
  compact = false
}) => {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState(null);
  const [error, setError] = useState(null);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  
  const t = {
    title: language === 'cs' ? 'Kontakty mise' : 'Mission Contacts',
    pilot: language === 'cs' ? 'Pilot' : 'Pilot',
    organization: language === 'cs' ? 'Organizace' : 'Organization',
    insurance: language === 'cs' ? 'Pojištění' : 'Insurance',
    emergency: language === 'cs' ? 'Nouzové kontakty' : 'Emergency Contacts',
    noContacts: language === 'cs' ? 'Žádné kontakty k zobrazení' : 'No contacts to display',
    error: language === 'cs' ? 'Chyba při načítání kontaktů' : 'Error loading contacts',
    staticContacts: language === 'cs' ? 'Standardní linky' : 'Standard Lines',
    orgContacts: language === 'cs' ? 'Kontakty organizace' : 'Organization Contacts',
    exportPdf: language === 'cs' ? 'Export pro ConOps' : 'Export for ConOps'
  };
  
  useEffect(() => {
    if (missionId) {
      fetchContacts();
    }
  }, [missionId, language]);
  
  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/missions/${missionId}/contacts?language=${language}`);
      setContacts(response.data);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Card data-testid="mission-contacts-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            {t.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="w-8 h-8 rounded-full" />
              <div className="space-y-1 flex-1">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className="border-red-200" data-testid="mission-contacts-card">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600">{t.error}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={fetchContacts}>
            {language === 'cs' ? 'Zkusit znovu' : 'Try again'}
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  // Count static vs org emergency contacts
  const staticEmergency = contacts?.emergency_contacts?.filter(c => c.source === 'static') || [];
  const orgEmergency = contacts?.emergency_contacts?.filter(c => c.source !== 'static') || [];
  
  return (
    <Card data-testid="mission-contacts-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-5 h-5" />
          {t.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pilot */}
        {contacts?.pilot && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t.pilot}
            </h4>
            <ContactItem 
              contact={contacts.pilot} 
              role="PIC"
              icon={User}
              language={language}
            />
          </div>
        )}
        
        {/* Organization */}
        {contacts?.organization && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t.organization}
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Building className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{contacts.organization.name}</span>
                {contacts.organization.operator_id && (
                  <Badge variant="outline" className="text-xs">
                    {contacts.organization.operator_id}
                  </Badge>
                )}
              </div>
              {contacts.organization.responsible_person && (
                <ContactItem 
                  contact={contacts.organization.responsible_person}
                  role={language === 'cs' ? 'Odpovědná osoba' : 'Responsible Person'}
                  icon={User}
                  language={language}
                />
              )}
              {contacts.organization.primary_contact && (
                <ContactItem 
                  contact={contacts.organization.primary_contact}
                  role={language === 'cs' ? 'Primární kontakt' : 'Primary Contact'}
                  icon={User}
                  language={language}
                />
              )}
            </div>
          </div>
        )}
        
        {/* Insurance */}
        {contacts?.insurance && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {t.insurance}
            </h4>
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-green-600" />
                <span className="font-medium text-sm">{contacts.insurance.provider}</span>
              </div>
              <div className="text-xs text-gray-600 space-y-0.5">
                <p>
                  {language === 'cs' ? 'Číslo pojistky' : 'Policy'}: {contacts.insurance.policy_number}
                </p>
                <p>
                  {language === 'cs' ? 'Krytí' : 'Coverage'}: {contacts.insurance.coverage_amount?.toLocaleString()} {contacts.insurance.currency}
                </p>
              </div>
              {contacts.insurance.contact && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <ContactItem 
                    contact={contacts.insurance.contact}
                    icon={Phone}
                    language={language}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Emergency Contacts */}
        <Collapsible open={emergencyOpen} onOpenChange={setEmergencyOpen}>
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-lg">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                {t.emergency}
                <Badge variant="secondary" className="text-xs">
                  {contacts?.emergency_contacts?.length || 0}
                </Badge>
              </h4>
              {emergencyOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 mt-2">
            {/* Organization emergency contacts */}
            {orgEmergency.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-1">{t.orgContacts}</p>
                <div className="space-y-1">
                  {orgEmergency.map((contact, idx) => (
                    <EmergencyContactItem key={idx} contact={contact} language={language} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Static emergency contacts */}
            {staticEmergency.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-1">{t.staticContacts}</p>
                <div className="space-y-1">
                  {staticEmergency.map((contact, idx) => (
                    <EmergencyContactItem key={idx} contact={contact} language={language} />
                  ))}
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default MissionContactsCard;
